import { Button } from "@/components/ui/button";
import MainContainer from "@/Elements/MainContainer";
import Navbar from "@/Elements/Navbar";
import Arc from "@/lib/Arc";
import { useAuth } from "@/Services/AuthContext";
import { ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// New component for the stylized membership card
function MembershipCard({ membership }) {
    const isActive = new Date(membership.expiry_date) > new Date();
    const logoUrl = "https://toontopia.in/Toontopia-01.png"; // Provided logo link

    // --- Conditional Background Logic ---
    let cardBackgroundClass;
    let accentColorClass = 'text-yellow-300'; // Default accent color for text

    if (membership.membership_type?.toLowerCase() === 'bronze') {
        // Use a brown/golden gradient to match the new image
        cardBackgroundClass = 'bg-gradient-to-br from-[#8B5F3B] to-[#A07B4F]';
        // Adjust accent color for better contrast on the bronze background
        accentColorClass = 'text-white';
    } else {
        // Use the default deep purple/violet gradient for other types (e.g., silver, gold)
        cardBackgroundClass = 'bg-gradient-to-br from-[#533456] to-[#452A4A]';
    }
    // ------------------------------------

    return (
        <div
            key={membership.id}
            // Apply the determined background class
            className={`relative ${cardBackgroundClass} rounded-2xl shadow-xl p-6 h-full min-h-[300px] overflow-hidden text-white border border-white/20 transform hover:scale-[1.02] transition-transform duration-200 ease-in-out`}
        >
            {/* Minimalistic star/speckle effect, mimicking the image background */}
            <div className="absolute inset-0 opacity-30">
                <div className="absolute top-4 right-8 w-1 h-1 bg-yellow-400 rounded-full shadow-[0_0_8px_#FBBF24]"></div>
                <div className="absolute top-1/2 left-1/4 w-0.5 h-0.5 bg-yellow-300 rounded-full shadow-[0_0_6px_#FBBF24]"></div>
                <div className="absolute bottom-6 right-20 w-1.5 h-1.5 bg-yellow-500 rounded-full shadow-[0_0_10px_#FBBF24]"></div>
                <div className="absolute bottom-1/4 left-10 w-1 h-1 bg-yellow-400 rounded-full shadow-[0_0_8px_#FBBF24]"></div>
                {/* Additional star elements */}
                <div className="absolute top-20 left-16 w-0.5 h-0.5 bg-yellow-200 rounded-full shadow-[0_0_4px_#FBBF24]"></div>
                <div className="absolute bottom-10 right-10 w-1 h-1 bg-yellow-400 rounded-full shadow-[0_0_8px_#FBBF24]"></div>
            </div>

            {/* Logo and Type */}
            <div className="flex justify-between items-start  relative z-10">
                <h2 className="text-2xl font-extrabold capitalize mt-3 text-white">
                    {membership.membership_type}
                </h2>
                {/* Logo - sized and placed as an image element */}
                <img
                    src={logoUrl}
                    alt="Toontopia Logo"
                    className="w-24 h-auto"
                />
            </div>

            {/* Content Section */}
            <div className="space-y-2 relative z-10">
                <p className="text-lg font-semibold text-center mt-3 ">
                    {' '}
                    {/* The funpass code background is slightly adjusted for bronze visibility */}
                    <span className="font-mono text-xl    rounded tracking-widest block mt-1">
                        {membership.funpass_code}
                    </span>
                </p>
                {/* <p>
                    
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ml-1 ${isActive
                            ? 'bg-green-600 text-white'
                            : 'bg-red-600 text-white'
                        }`}>
                        {isActive ? 'Active' : 'Expired'}
                    </span>
                </p> */}

                <div className="mt-3">
                    <p>
                        <span className="font-semibold">Expiry Date:</span>{' '}
                        {new Date(membership.expiry_date).toLocaleDateString()}
                    </p>
                    {membership.total_hours !== null && (
                        <p>
                            <span className="font-semibold">Remaining Hours:</span>{' '}
                            {membership.remaining_hours} / {membership.total_hours}
                        </p>
                    )}
                    <p>
                        <span className="font-semibold">Amount Paid:</span>{' '}
                        {/* Use the conditional accent color class */}
                        <span className={`font-bold ${accentColorClass}`}>
                            ${membership.amount_paid.toFixed(2)}
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
}

// Main MembershipScreen component
function MembershipScreen() {
    const navigate = useNavigate();
    // Assuming useAuth provides a user object and authentication status
    const { user, isAuthenticated, loading, login, logout } = useAuth();
    const [memberships, setMemberships] = useState([]);

    useEffect(() => {
        // Only fetch if authenticated and user uid is available
        if (isAuthenticated && user?.uid) {
            Arc().read('data', 'reader', [
                "entity=memberships",
                `where=[[guardian_uid,${user.uid}]]`
            ]).then(res => setMemberships(res.result)).catch(console.error);
        }
    }, [isAuthenticated, user?.uid]); // Dependency array updated

    return (
        // Page background is white, text is dark grey
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar></Navbar>

            <MainContainer>
                {/* Button updated to look appropriate on a white background */}
                <Button variant="ghost" onClick={() => { navigate('/home') }} className="text-gray-800 hover:bg-gray-100">
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Back to Home
                </Button>
                <h1 className='text-3xl md:text-4xl lg:text-5xl font-extrabold drop-shadow-lg mt-2'>
                    Your Memberships
                </h1>
                <p className='mt-1'>
                    Manage your active and expired memberships here.
                </p>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {memberships.length > 0 ? (
                        memberships.map((membership) => (
                            <MembershipCard key={membership.id} membership={membership} />
                        ))
                    ) : (
                        <p className="col-span-full text-center text-lg">
                            No memberships found.
                        </p>
                    )}
                </div>
            </MainContainer>
        </div>
    );
}
export default MembershipScreen;